import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Input,
  Tag,
  Tabs,
  Select,
  Table,
  Row,
  Col,
  Progress,
  Statistic,
  Slider,
  InputNumber,
  Switch,
  Alert,
  Badge,
  Tooltip,
  Modal,
  Form,
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  DashboardOutlined,
  ControlOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  LaptopOutlined,
  SlidersOutlined,
  InfoCircleOutlined,
  EditOutlined,
  KeyOutlined,
  LockOutlined,
  UserOutlined,
  GlobalOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import { App as AntApp } from 'antd';
import { admin } from '../../services/api';

const { Title, Text, Paragraph } = Typography;

const SswIntegracao = () => {
  const { message } = AntApp.useApp();
  const [chave, setChave] = useState('');
  const [danfeResult, setDanfeResult] = useState(null);
  const [consulting, setConsulting] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsVisible, setLogsVisible] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);

  const [logDates, setLogDates] = useState([]);
  const [logFilter, setLogFilter] = useState({ date: '', orgId: '', q: '' });
  const [logResults, setLogResults] = useState([]);
  const [logsSearching, setLogsSearching] = useState(false);

  // Apenas funcionários digitais (isDigitalWorker=true) possuem acesso ao SSW
  const [sswUsers, setSswUsers] = useState([]);
  const [sswUsersLoading, setSswUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Edição de Credenciais do Funcionário Digital
  const [branches, setBranches] = useState([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingWorker, setEditingWorker] = useState(null);
  const [savingWorker, setSavingWorker] = useState(false);
  const [editForm] = Form.useForm();

  // Semáforo Global e Monitoramento de Concorrência
  const [poolStatus, setPoolStatus] = useState(null);
  const [loadingPool, setLoadingPool] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [customLimit, setCustomLimit] = useState(8);
  const [savingLimit, setSavingLimit] = useState(false);
  const autoRefreshRef = useRef(null);

  useEffect(() => {
    loadSswUsers();
    loadBranches();
    loadLogDates();
    loadPoolStatus(true);
  }, []);

  // Sincroniza formulário sempre que o modal de edição for aberto ou o worker for alterado
  useEffect(() => {
    if (editModalVisible && editingWorker) {
      editForm.setFieldsValue({
        name: editingWorker.name || '',
        assignedBranchId: editingWorker.assignedBranchId || undefined,
        erpDominio: editingWorker.erpDominio || '',
        erpCpf: editingWorker.erpCpf || '',
        erpUsuario: editingWorker.erpUsuario || '',
        erpSenha: editingWorker.erpSenha || '',
      });
    }
  }, [editModalVisible, editingWorker, editForm]);

  // Timer de Auto-Refresh (a cada 3 segundos)
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        loadPoolStatus(false);
      }, 3000);
    } else if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
    }
    return () => {
      if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    };
  }, [autoRefresh]);

  const loadPoolStatus = async (showLoading = false) => {
    try {
      if (showLoading) setLoadingPool(true);
      const res = await admin.sswPoolStatus();
      setPoolStatus(res);
      if (res?.semaphore?.maxConcurrentBrowsers && !savingLimit) {
        setCustomLimit(res.semaphore.maxConcurrentBrowsers);
      }
    } catch (e) {
      if (showLoading) {
        message.error(e.message || 'Erro ao obter status do semáforo global');
      }
    } finally {
      if (showLoading) setLoadingPool(false);
    }
  };

  const handleSaveLimit = async () => {
    try {
      setSavingLimit(true);
      const res = await admin.sswUpdatePoolLimit(customLimit);
      message.success(
        res?.message || `Limite do semáforo atualizado para ${customLimit} navegadores!`,
      );
      await loadPoolStatus(false);
    } catch (e) {
      message.error(e.message || 'Erro ao atualizar limite de concorrência');
    } finally {
      setSavingLimit(false);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await admin.getBranches();
      setBranches(res || []);
    } catch (e) {
      console.warn('Erro ao carregar filiais:', e);
    }
  };

  const loadSswUsers = async () => {
    try {
      setSswUsersLoading(true);
      const users = await admin.getDigitalWorkers();
      const mapped = (users || []).map((u) => ({
        id: u.id,
        codigo: u.codigo,
        name: u.name,
        erpDominio: u.erpDominio,
        erpCpf: u.erpCpf,
        erpUsuario: u.erpUsuario,
        erpSenha: u.erpSenha || '',
        assignedBranchId: u.assignedBranchId || u.assignedBranch?.id,
        branch: u.assignedBranch?.name || 'Não atribuída',
        sigla: u.assignedBranch?.sigla || '-',
      }));
      setSswUsers(mapped);
      setSelectedUserId((prev) =>
        mapped.some((u) => u.id === prev) ? prev : mapped[0]?.id || null,
      );
    } catch (e) {
      message.warning('Não foi possível carregar funcionários digitais cadastrados');
    } finally {
      setSswUsersLoading(false);
    }
  };

  const handleEditClick = async (record) => {
    setEditingWorker(record);
    if (branches.length === 0) {
      await loadBranches();
    }
    setEditModalVisible(true);
    setTimeout(() => {
      editForm.setFieldsValue({
        name: record.name || '',
        assignedBranchId: record.assignedBranchId || undefined,
        erpDominio: record.erpDominio || '',
        erpCpf: record.erpCpf || '',
        erpUsuario: record.erpUsuario || '',
        erpSenha: record.erpSenha || '',
      });
    }, 50);
  };

  const handleSaveWorker = async () => {
    try {
      const values = await editForm.validateFields();
      setSavingWorker(true);
      await admin.updateUser(editingWorker.id, {
        name: values.name,
        assignedBranchId: values.assignedBranchId,
        erpDominio: values.erpDominio,
        erpCpf: values.erpCpf,
        erpUsuario: values.erpUsuario,
        erpSenha: values.erpSenha,
      });
      message.success('Dados de login do ERP atualizados com sucesso!');
      setEditModalVisible(false);
      setEditingWorker(null);
      editForm.resetFields();
      await loadSswUsers();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || 'Erro ao atualizar dados de login do ERP');
    } finally {
      setSavingWorker(false);
    }
  };

  const consultarDanfe = async () => {
    if (!chave || chave.length !== 44) {
      message.warning('Chave de acesso deve ter 44 dígitos');
      return;
    }
    try {
      setConsulting(true);
      setDanfeResult(null);
      const res = await admin.sswConsultarDanfe(chave, { userId: selectedUserId });
      setDanfeResult(res);
    } catch (e) {
      message.error(e.message || 'Erro na consulta');
    } finally {
      setConsulting(false);
    }
  };

  const loadLogs = async () => {
    try {
      setLogsLoading(true);
      const res = await admin.sswLogs();
      setLogs(res?.logs || []);
      setLogsVisible(true);
    } catch (e) {
      message.error(e.message || 'Erro ao carregar logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadLogDates = async () => {
    try {
      const res = await admin.sswLogsDates();
      const dates = res?.dates || [];
      setLogDates(dates);
      setLogFilter((f) => (f.date ? f : { ...f, date: dates[0] || '' }));
    } catch {
      /* datas opcionais */
    }
  };

  const searchLogs = async () => {
    try {
      setLogsSearching(true);
      const res = await admin.sswLogsSearch(logFilter);
      setLogResults(res?.logs || []);
    } catch (e) {
      message.error(e.message || 'Erro ao buscar logs');
    } finally {
      setLogsSearching(false);
    }
  };

  // Extrações de métricas do Semáforo e VPS
  const semaphore = poolStatus?.semaphore || {
    activeBrowsers: 0,
    maxConcurrentBrowsers: 8,
    availableSlots: 8,
    utilizationPercent: 0,
    busyWorkerCount: 0,
    waitQueueLength: 0,
  };

  const vps = poolStatus?.vps || {
    totalVpsRamMb: 8192,
    estimatedMemoryUsedMb: 0,
    estimatedMemoryFreeMb: 8192,
    memoryPercent: 0,
    averagePerBrowserMb: 250,
  };

  const runningProcesses = poolStatus?.runningProcesses || [];
  const queuedRequests = poolStatus?.queuedRequests || [];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Space direction="vertical" size={2}>
          <Title level={3} style={{ margin: 0 }}>
            SSW - Automação & Semáforo Global
          </Title>
          <Text type="secondary">
            Gestão de concorrência de navegadores Playwright e controle de hardware da VPS
          </Text>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              loadSswUsers();
              loadPoolStatus(true);
            }}
            loading={sswUsersLoading || loadingPool}
          >
            Atualizar Dados
          </Button>
        </Space>
      </div>

      <Card
        title="Funcionários Digitais Cadastrados (por filial)"
        style={{ marginBottom: 16 }}
        extra={
          <Text type="secondary">
            {sswUsers.length} funcionário(s) digital(is) configurado(s)
          </Text>
        }
      >
        <Table
          size="small"
          rowKey="id"
          loading={sswUsersLoading}
          dataSource={sswUsers}
          pagination={false}
          locale={{
            emptyText:
              'Nenhum funcionário digital com credenciais SSW cadastrado. Cadastre o CPF/usuário/senha do ERP na tela de Usuários marcando como Funcionário Digital.',
          }}
          scroll={{ x: 'max-content' }}
          columns={[
            { title: 'Nome', dataIndex: 'name', key: 'name' },
            {
              title: 'Código',
              dataIndex: 'codigo',
              key: 'codigo',
              width: 100,
              render: (v) => <Tag color="blue">{v || 'N/A'}</Tag>,
            },
            {
              title: 'Filial',
              dataIndex: 'branch',
              key: 'branch',
              width: 180,
              render: (branch, r) => (
                <Space>
                  <Tag color="cyan">{r.sigla}</Tag>
                  <span>{branch}</span>
                </Space>
              ),
            },
            {
              title: 'Domínio',
              dataIndex: 'erpDominio',
              key: 'erpDominio',
              width: 120,
              render: (v) => (v ? <Tag>{v}</Tag> : '-'),
            },
            { title: 'CPF ERP', dataIndex: 'erpCpf', key: 'erpCpf', width: 140 },
            { title: 'Usuário ERP', dataIndex: 'erpUsuario', key: 'erpUsuario', width: 140 },
            {
              title: 'Senha ERP',
              dataIndex: 'erpSenha',
              key: 'erpSenha',
              width: 120,
              render: (v) => (v ? <Tag color="gold">••••••••</Tag> : <Text type="secondary">-</Text>),
            },
            {
              title: 'Ações',
              key: 'actions',
              width: 100,
              align: 'center',
              render: (_, record) => (
                <Button
                  type="primary"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditClick(record)}
                >
                  Editar
                </Button>
              ),
            },
          ]}
        />
      </Card>

      {/* Modal de Edição de Dados de Login do ERP */}
      <Modal
        title={
          <Space>
            <KeyOutlined style={{ color: '#faad14' }} />
            <span>Editar Dados de Login do ERP - Funcionário Digital</span>
          </Space>
        }
        style={{ top: 0 }}
        open={editModalVisible}
        onCancel={() => {
          if (!savingWorker) {
            setEditModalVisible(false);
            setEditingWorker(null);
            editForm.resetFields();
          }
        }}
        onOk={handleSaveWorker}
        confirmLoading={savingWorker}
        okText="Salvar Alterações"
        cancelText="Cancelar"
        destroyOnClose
        width={560}
      >
        {editingWorker && (
          <Form
            key={editingWorker.id}
            form={editForm}
            layout="vertical"
            style={{ marginTop: 16 }}
            initialValues={{
              name: editingWorker.name || '',
              assignedBranchId: editingWorker.assignedBranchId || undefined,
              erpDominio: editingWorker.erpDominio || '',
              erpCpf: editingWorker.erpCpf || '',
              erpUsuario: editingWorker.erpUsuario || '',
              erpSenha: editingWorker.erpSenha || '',
            }}
          >
            <Alert
              message="Credenciais SSW do Funcionário Digital"
              description="Estes dados são utilizados pelas rotinas Playwright para autenticação no ERP SSW (emissões, consultas e rotinas em background)."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="Nome do Funcionário Digital"
                  rules={[{ required: true, message: 'Informe o nome' }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Nome do robô" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="assignedBranchId"
                  label="Filial Vinculada"
                  rules={[{ required: true, message: 'Selecione a filial' }]}
                >
                  <Select placeholder="Selecione a filial">
                    {branches.map((b) => (
                      <Select.Option key={b.id} value={b.id}>
                        {b.sigla ? `[${b.sigla}] ` : ''}{b.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="erpDominio"
                  label="Domínio no ERP SSW"
                  rules={[{ required: true, message: 'Informe o domínio (ex: CLD)' }]}
                >
                  <Input prefix={<GlobalOutlined />} placeholder="Ex: CLD" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="erpCpf"
                  label="CPF do Usuário ERP"
                >
                  <Input prefix={<IdcardOutlined />} placeholder="Apenas dígitos ou formatado" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="erpUsuario"
                  label="Usuário no ERP SSW"
                  rules={[{ required: true, message: 'Informe o usuário no ERP' }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Ex: jociane1" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="erpSenha"
                  label="Senha usada no ERP SSW"
                  rules={[{ required: true, message: 'Informe a senha no ERP' }]}
                  extra="Senha de 8 dígitos cadastrada no SSW."
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Senha no ERP SSW"
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>

      <Tabs
        defaultActiveKey="controle"
        items={[
          {
            key: 'controle',
            label: (
              <span>
                <DashboardOutlined /> Controle do Navegador & Semáforo
              </span>
            ),
            children: (
              <>
                {/* Painel do Semáforo Global */}
                <Card
                  title={
                    <Space align="center">
                      <ControlOutlined style={{ color: '#1677ff' }} />
                      <span>Semáforo Global de Concorrência</span>
                      {semaphore.activeBrowsers > 0 && (
                        <Badge
                          status="processing"
                          text={`${semaphore.activeBrowsers} ativo(s)`}
                        />
                      )}
                    </Space>
                  }
                  extra={
                    <Space size="middle" align="center">
                      <Space size="small">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Auto-atualizar (3s):
                        </Text>
                        <Switch
                          size="small"
                          checked={autoRefresh}
                          onChange={setAutoRefresh}
                        />
                      </Space>
                      <Button
                        size="small"
                        icon={<ReloadOutlined spin={loadingPool} />}
                        onClick={() => loadPoolStatus(true)}
                        loading={loadingPool}
                      >
                        Atualizar Agora
                      </Button>
                      {poolStatus?.timestamp && (
                        <Tag color="default" style={{ fontSize: 11 }}>
                          {new Date(poolStatus.timestamp).toLocaleTimeString()}
                        </Tag>
                      )}
                    </Space>
                  }
                  style={{ marginBottom: 16 }}
                >
                  <Alert
                    message="Controle de Concorrência Global & Prevenção de Out-Of-Memory (OOM)"
                    description="O Semáforo Global limita o teto de processos concorrentes no Playwright. Cada robô digital mantém 1 instância Chromium conectada com suporte a multiplexação de abas (novas abas isoladas por processo), reutilizando a sessão de login e fechando apenas suas respectivas abas ao término da tarefa."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  {/* Cards de Métricas da VPS e Semáforo */}
                  <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
                    <Col xs={24} sm={12} lg={6}>
                      <Card
                        size="small"
                        bordered
                        style={{
                          background: '#fafafa',
                          borderColor:
                            semaphore.utilizationPercent > 85 ? '#ff4d4f' : '#d9d9d9',
                        }}
                      >
                        <Statistic
                          title="Navegadores Playwright (Chromium)"
                          value={semaphore.activeBrowsers}
                          suffix={`/ ${semaphore.maxConcurrentBrowsers}`}
                          valueStyle={{
                            color:
                              semaphore.utilizationPercent > 85
                                ? '#ff4d4f'
                                : semaphore.utilizationPercent > 60
                                  ? '#faad14'
                                  : '#52c41a',
                            fontWeight: 'bold',
                          }}
                        />
                        <Progress
                          percent={semaphore.utilizationPercent}
                          size="small"
                          status={
                            semaphore.utilizationPercent > 85
                              ? 'exception'
                              : semaphore.utilizationPercent > 60
                                ? 'active'
                                : 'normal'
                          }
                          strokeColor={
                            semaphore.utilizationPercent > 85
                              ? '#ff4d4f'
                              : semaphore.utilizationPercent > 60
                                ? '#faad14'
                                : '#52c41a'
                          }
                        />
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                          Processos / Abas: <Text strong>{semaphore.activeProcesses ?? semaphore.activeBrowsers}</Text> | Livres:{' '}
                          <Text strong>{semaphore.availableSlots}</Text>
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Card size="small" bordered style={{ background: '#fafafa' }}>
                        <Statistic
                          title="RAM Estimada"
                          value={vps.estimatedMemoryUsedMb}
                          suffix={`/ ${vps.totalVpsRamMb} MB`}
                          valueStyle={{ color: '#1677ff', fontWeight: 'bold' }}
                        />
                        <Progress
                          percent={vps.memoryPercent}
                          size="small"
                          strokeColor="#1677ff"
                        />
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                          Média de ~200 MB por navegador + 50 MB por aba
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Card size="small" bordered style={{ background: '#fafafa' }}>
                        <Statistic
                          title="Robôs Digitais Ativos"
                          value={semaphore.busyWorkerCount}
                          suffix={`/ ${sswUsers.length}`}
                          valueStyle={{
                            color:
                              semaphore.busyWorkerCount > 0 ? '#722ed1' : '#595959',
                            fontWeight: 'bold',
                          }}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Tag color="cyan">Multiplexação de Abas</Tag>
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                          Abas isoladas por processo no mesmo navegador
                        </div>
                      </Card>
                    </Col>

                    <Col xs={24} sm={12} lg={6}>
                      <Card
                        size="small"
                        bordered
                        style={{
                          background: '#fafafa',
                          borderColor:
                            semaphore.waitQueueLength > 0 ? '#faad14' : '#d9d9d9',
                        }}
                      >
                        <Statistic
                          title="Fila de Espera Global"
                          value={semaphore.waitQueueLength}
                          suffix="em fila"
                          valueStyle={{
                            color:
                              semaphore.waitQueueLength > 0 ? '#fa8c16' : '#52c41a',
                            fontWeight: 'bold',
                          }}
                        />
                        <div style={{ marginTop: 8 }}>
                          {semaphore.waitQueueLength > 0 ? (
                            <Tag color="warning">Aguardando Vaga</Tag>
                          ) : (
                            <Tag color="success">Sem Fila (Atendimento Imediato)</Tag>
                          )}
                        </div>
                        <div style={{ marginTop: 6, fontSize: 12, color: '#8c8c8c' }}>
                          Timeout configurado: 45s
                        </div>
                      </Card>
                    </Col>
                  </Row>

                  {/* Card de Configuração do Limite do Semáforo */}
                  <Card
                    type="inner"
                    title={
                      <Space>
                        <SlidersOutlined />
                        <span>Ajuste do Limite do Semáforo Global</span>
                      </Space>
                    }
                    style={{ marginBottom: 20 }}
                  >
                    <Row gutter={[24, 16]} align="middle">
                      <Col xs={24} md={12}>
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>
                            Limite Máximo de Navegadores Simultâneos no Servidor:
                          </Text>
                          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                            Recomendado para VPS Ubuntu 8GB:{' '}
                            <Text strong type="success">
                              6 a 8 navegadores
                            </Text>{' '}
                            (~1,5 a 2,0 GB de RAM reservados para Playwright).
                          </div>
                        </div>
                        <Row gutter={16} align="middle">
                          <Col span={16}>
                            <Slider
                              min={1}
                              max={20}
                              value={customLimit}
                              onChange={setCustomLimit}
                              marks={{
                                1: '1',
                                6: '6 (Ideal)',
                                8: '8 (Padrão)',
                                12: '12',
                                20: '20 (Máx)',
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <InputNumber
                              min={1}
                              max={20}
                              value={customLimit}
                              onChange={(v) => v && setCustomLimit(v)}
                              style={{ width: '100%' }}
                            />
                          </Col>
                        </Row>
                      </Col>
                      <Col xs={24} md={12}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                          <Button
                            type="primary"
                            icon={<SlidersOutlined />}
                            onClick={handleSaveLimit}
                            loading={savingLimit}
                            disabled={customLimit === semaphore.maxConcurrentBrowsers}
                          >
                            Salvar Novo Limite ({customLimit} simultâneos)
                          </Button>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            A alteração tem efeito imediato na memória do backend sem
                            necessidade de reiniciar a aplicação.
                          </Text>
                        </Space>
                      </Col>
                    </Row>
                  </Card>

                  {/* Tabela de Processos em Execução */}
                  <Card
                    type="inner"
                    title={
                      <Space>
                        <SyncOutlined spin={runningProcesses.length > 0} />
                        <span>Processos & Abas Playwright em Execução</span>
                        <Badge
                          count={runningProcesses.length}
                          style={{ backgroundColor: '#52c41a' }}
                        />
                      </Space>
                    }
                    style={{ marginBottom: 20 }}
                  >
                    <Table
                      size="small"
                      rowKey={(r) => r.processId || r.workerId}
                      dataSource={runningProcesses}
                      pagination={false}
                      locale={{
                        emptyText:
                          'Nenhum processo Playwright em execução no momento. O servidor está operando em repouso.',
                      }}
                      columns={[
                        {
                          title: 'Processo / ID',
                          dataIndex: 'processId',
                          key: 'processId',
                          width: 140,
                          render: (pid) => (
                            <Tag color="purple">{pid ? pid.substring(0, 16) : 'proc'}</Tag>
                          ),
                        },
                        {
                          title: 'Funcionário Digital',
                          dataIndex: 'workerName',
                          key: 'workerName',
                          render: (name, r) => (
                            <Space direction="vertical" size={0}>
                              <Text strong>{name}</Text>
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                {r.workerId}
                              </Text>
                            </Space>
                          ),
                        },
                        {
                          title: 'Filial',
                          dataIndex: 'siglaFilial',
                          key: 'siglaFilial',
                          width: 100,
                          render: (sigla) => <Tag color="blue">{sigla}</Tag>,
                        },
                        {
                          title: 'Usuário ERP SSW',
                          dataIndex: 'erpUsuario',
                          key: 'erpUsuario',
                          width: 140,
                        },
                        {
                          title: 'Domínio',
                          dataIndex: 'erpDominio',
                          key: 'erpDominio',
                          width: 100,
                          render: (dom) => (dom ? <Tag>{dom}</Tag> : '-'),
                        },
                        {
                          title: 'Início',
                          dataIndex: 'startedAt',
                          key: 'startedAt',
                          width: 110,
                          render: (d) => new Date(d).toLocaleTimeString(),
                        },
                        {
                          title: 'Tempo em Execução',
                          dataIndex: 'durationSeconds',
                          key: 'durationSeconds',
                          width: 150,
                          render: (s) => (
                            <Tag icon={<ClockCircleOutlined />} color="processing">
                              {s} seg
                            </Tag>
                          ),
                        },
                        {
                          title: 'RAM Estimada',
                          dataIndex: 'estimatedRamMb',
                          key: 'estimatedRamMb',
                          width: 130,
                          render: (v) => <Tag color="geekblue">~{v} MB</Tag>,
                        },
                        {
                          title: 'Status',
                          key: 'status',
                          width: 130,
                          render: () => (
                            <Badge status="processing" text="Em Execução" />
                          ),
                        },
                      ]}
                    />
                  </Card>

                  {/* Tabela de Fila de Espera */}
                  <Card
                    type="inner"
                    title={
                      <Space>
                        <ClockCircleOutlined />
                        <span>Fila de Espera (Aguardando Vaga no Semáforo)</span>
                        <Badge
                          count={queuedRequests.length}
                          style={{
                            backgroundColor:
                              queuedRequests.length > 0 ? '#faad14' : '#d9d9d9',
                          }}
                        />
                      </Space>
                    }
                  >
                    <Table
                      size="small"
                      rowKey="id"
                      dataSource={queuedRequests}
                      pagination={false}
                      locale={{
                        emptyText:
                          'Nenhuma requisição na fila de espera. Todas as operações estão sendo atendidas imediatamente.',
                      }}
                      columns={[
                        {
                          title: 'Posição',
                          key: 'pos',
                          width: 80,
                          render: (_, __, i) => (
                            <Tag color="orange">#{i + 1}</Tag>
                          ),
                        },
                        {
                          title: 'ID da Requisição',
                          dataIndex: 'id',
                          key: 'id',
                          render: (id) => <Text code>{id}</Text>,
                        },
                        {
                          title: 'Filial Solicitada',
                          dataIndex: 'unidade',
                          key: 'unidade',
                          width: 140,
                          render: (u) => <Tag color="cyan">{u}</Tag>,
                        },
                        {
                          title: 'Entrada na Fila',
                          dataIndex: 'queuedAt',
                          key: 'queuedAt',
                          width: 120,
                          render: (d) => new Date(d).toLocaleTimeString(),
                        },
                        {
                          title: 'Tempo na Fila',
                          dataIndex: 'waitSeconds',
                          key: 'waitSeconds',
                          width: 130,
                          render: (s) => (
                            <Tag color="warning" icon={<ClockCircleOutlined />}>
                              {s} seg
                            </Tag>
                          ),
                        },
                        {
                          title: 'Timeout Limite',
                          dataIndex: 'timeoutMs',
                          key: 'timeoutMs',
                          width: 130,
                          render: (ms) => `${Math.round(ms / 1000)}s`,
                        },
                        {
                          title: 'Situação',
                          key: 'situacao',
                          width: 150,
                          render: () => (
                            <Tag color="warning">Aguardando Liberação</Tag>
                          ),
                        },
                      ]}
                    />
                  </Card>
                </Card>

                {/* Card de Teste Rápido de DANFE */}
                <Card title="Teste Rápido de Automação (Consulta de DANFE)">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Select
                        style={{ minWidth: 260 }}
                        placeholder="Selecione um acesso SSW"
                        value={selectedUserId || undefined}
                        onChange={setSelectedUserId}
                        loading={sswUsersLoading}
                        options={sswUsers.map((u) => ({
                          value: u.id,
                          label: `${u.name} (${u.branch}) — ${u.erpUsuario}`,
                        }))}
                        allowClear
                      />
                      <Input
                        placeholder="Chave de acesso da NFe (44 dígitos)"
                        maxLength={44}
                        style={{ width: 360 }}
                        value={chave}
                        onChange={(e) =>
                          setChave(e.target.value.replace(/\D/g, ''))
                        }
                      />
                      <Button
                        type="primary"
                        icon={<SearchOutlined />}
                        onClick={consultarDanfe}
                        loading={consulting}
                        disabled={!sswUsers.length}
                      >
                        Consultar DANFE
                      </Button>
                    </div>
                    {danfeResult && (
                      <Card size="small" title="Resultado" style={{ marginTop: 8 }}>
                        <pre
                          style={{
                            maxHeight: 400,
                            overflow: 'auto',
                            background: '#f5f5f5',
                            padding: 12,
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          {JSON.stringify(danfeResult, null, 2)}
                        </pre>
                      </Card>
                    )}
                  </Space>
                </Card>
              </>
            ),
          },
          {
            key: 'logs',
            label: (
              <span>
                <FileTextOutlined /> Logs
              </span>
            ),
            children: (
              <Card title="Busca de Logs">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space wrap>
                    <Select
                      placeholder="Data"
                      style={{ width: 160 }}
                      value={logFilter.date || undefined}
                      onChange={(v) =>
                        setLogFilter((f) => ({ ...f, date: v || '' }))
                      }
                      options={logDates.map((d) => ({ value: d, label: d }))}
                      allowClear
                    />
                    <Input
                      placeholder="Organization ID"
                      style={{ width: 240 }}
                      value={logFilter.orgId}
                      onChange={(e) =>
                        setLogFilter((f) => ({ ...f, orgId: e.target.value }))
                      }
                      allowClear
                    />
                    <Input
                      placeholder="Texto (ex: login, danfe)"
                      style={{ width: 200 }}
                      value={logFilter.q}
                      onChange={(e) =>
                        setLogFilter((f) => ({ ...f, q: e.target.value }))
                      }
                      allowClear
                      onPressEnter={searchLogs}
                    />
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      onClick={searchLogs}
                      loading={logsSearching}
                    >
                      Buscar
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadLogDates}>
                      Datas
                    </Button>
                  </Space>
                  <Table
                    size="small"
                    rowKey={(r, i) => `${r.ts}-${i}`}
                    dataSource={logResults}
                    pagination={{ pageSize: 20 }}
                    locale={{ emptyText: 'Nenhum log encontrado' }}
                    columns={[
                      {
                        title: 'Data/Hora',
                        dataIndex: 'ts',
                        width: 180,
                        render: (v) => new Date(v).toLocaleString(),
                      },
                      {
                        title: 'Nível',
                        dataIndex: 'level',
                        width: 80,
                        render: (v) => (
                          <Tag
                            color={
                              v === 'ERROR'
                                ? 'red'
                                : v === 'WARN'
                                  ? 'orange'
                                  : 'blue'
                            }
                          >
                            {v}
                          </Tag>
                        ),
                      },
                      {
                        title: 'Org',
                        dataIndex: 'orgId',
                        width: 220,
                        render: (v) => v || '-',
                      },
                      { title: 'Mensagem', dataIndex: 'msg' },
                    ]}
                  />
                </Space>
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
};

export default SswIntegracao;
